import React, { useEffect, useState } from 'react';
import { dashboardAPI } from './resellerapi';

const STAT_CARDS = [
  { key: 'wallet_balance',   label: 'Wallet Balance',    format: '₹', color: '#10b981' },
  { key: 'sales_this_month', label: 'Sales This Month',  format: '',  color: '#3b82f6' },
  { key: 'total_customers',  label: 'Total Customers',   format: '',  color: '#f59e0b' },
  { key: 'total_earned',     label: 'All-time Earned',   format: '₹', color: '#a78bfa' },
];

const ResellerDashboard = ({ user }) => {
  const [stats,      setStats]      = useState(null);
  const [sales,      setSales]      = useState([]);
  const [leaders,    setLeaders]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r, l] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentSales(),
          dashboardAPI.getLeaderboard(),
        ]);
        setStats(s.data);
        setSales(r.data || []);
        setLeaders(l.data || []);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const myRank = leaders.findIndex(l => l.reseller_id === user?.id) + 1;
  const tierProgress = stats
    ? Math.min((stats.sales_this_month / 20) * 100, 100)
    : 0;
  const nextTierSales = stats ? Math.max(20 - stats.sales_this_month, 0) : 20;

  const statusClass = (status) => {
    const map = { Delivered: 'delivered', Shipped: 'shipped', 'In Transit': 'transit', Pending: 'pending' };
    return `status-badge ${map[status] || 'pending'}`;
  };

  if (loading) return (
    <div>
      <div className="dashboard-hero" style={{ height: 110 }}>
        <div className="skeleton" style={{ width: 300, height: 20, marginBottom: 10 }} />
        <div className="skeleton" style={{ width: 180, height: 14 }} />
      </div>
      <div className="dashboard-stats-row">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="dash-stat-card">
            <div className="skeleton" style={{ width: 80, height: 14, marginBottom: 10 }} />
            <div className="skeleton" style={{ width: 120, height: 28 }} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {error && <div className="res-error">{error}</div>}

      {/* ── WELCOME HERO ── */}
      <div className="dashboard-hero">
        <div>
          <p className="dh-greeting">Good day, partner 👋</p>
          <h2 className="dh-name">
            Welcome back, <span>{user?.name?.split(' ')[0] || 'Reseller'}</span>
          </h2>
          <p className="dh-tier-line">
            You're on <b>Bronze Tier</b> · {nextTierSales} more sales → Silver
          </p>
        </div>
        <div className="dh-progress-wrap">
          <div className="dh-progress-label">
            <span>Tier Progress</span>
            <span>{stats?.sales_this_month || 0}/20 sales</span>
          </div>
          <div className="dh-progress-bar">
            <div className="dh-progress-fill" style={{ width: `${tierProgress}%` }} />
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
            Reach 20 sales to unlock Silver (+2% commission)
          </p>
        </div>
      </div>

      {/* ── STAT CARDS ── */}
      <div className="dashboard-stats-row">
        {STAT_CARDS.map(({ key, label, format, color }) => (
          <div key={key} className="dash-stat-card" style={{ '--accent-color': color }}>
            <div className="dash-stat-label">{label}</div>
            <div className="dash-stat-val">
              {format}{stats ? Number(stats[key] || 0).toLocaleString('en-IN') : '—'}
            </div>
            {stats?.[`${key}_delta`] !== undefined && (
              <div className={`dash-stat-delta ${stats[`${key}_delta`] >= 0 ? 'pos' : 'neg'}`}>
                {stats[`${key}_delta`] >= 0 ? '↑' : '↓'} {Math.abs(stats[`${key}_delta`])}% vs last month
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── MAIN GRID ── */}
      <div className="dashboard-grid">

        {/* Recent Sales Table */}
        <div className="res-card">
          <div className="res-card-title">
            Recent Network Sales
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-3)', fontFamily: 'var(--font-body)' }}>
              Last 10 orders
            </span>
          </div>
          <div className="res-table-wrap">
            <table className="res-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sales.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>
                      No sales yet. Start sharing products!
                    </td>
                  </tr>
                ) : sales.map(s => (
                  <tr key={s.id}>
                    <td className="td-primary">{s.order_ref}</td>
                    <td>{s.customer_name}</td>
                    <td>{s.product_name}</td>
                    <td className="td-green">₹{Number(s.commission).toLocaleString('en-IN')}</td>
                    <td><span className={statusClass(s.status)}>{s.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="res-card">
          <div className="res-card-title">Top Resellers 🏆</div>
          <div className="leaderboard-list">
            {leaders.slice(0, 5).map((l, i) => (
              <div key={l.reseller_id} className={`leader-row ${l.reseller_id === user?.id ? 'is-me' : ''}`}>
                <div className={`leader-rank ${i === 0 ? 'top1' : i === 1 ? 'top2' : i === 2 ? 'top3' : ''}`}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                </div>
                <div className="leader-avatar">{l.name?.[0]?.toUpperCase()}</div>
                <span className="leader-name">
                  {l.name}
                  {l.reseller_id === user?.id && (
                    <span style={{ fontSize: 10, color: 'var(--em-400)', marginLeft: 5 }}>(You)</span>
                  )}
                </span>
                <span className="leader-amt">₹{Number(l.total_earned).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
          {myRank > 5 && (
            <div className="leader-row" style={{ marginTop: 8, borderTop: '1px solid var(--border-dim)', paddingTop: 8 }}>
              <div className="leader-rank">#{myRank}</div>
              <div className="leader-avatar">{user?.name?.[0]?.toUpperCase()}</div>
              <span className="leader-name">You</span>
              <span className="leader-amt">₹{Number(stats?.total_earned || 0).toLocaleString('en-IN')}</span>
            </div>
          )}
          <div className="motivation-chip">
            {nextTierSales > 0
              ? <>Sell <b>{nextTierSales} more products</b> to unlock Silver Tier + 2% extra commission!</>
              : <>🎉 You've hit Silver Tier target this month!</>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResellerDashboard;